import { trpc, trpc2 } from "../../../utils/trpc";
import FilterAssigneeDropdown from "../FilterAssigneeDropdown";
import { stringifyCompletedAtFilterRelation } from "./utils";
import {
  ClientTag,
  ClientWorkflow,
  CompletedAtFilterRelations,
  CreatedAtRelations,
  ProcessFilterZod,
  TimeToResolutionTimePeriods,
} from "@avenueops/shared-types";
import { DateTimePicker, DropdownMultiSelect, DropdownSelect, TextInput, Avatar } from "@avenueops/web-components";
import { MonitorGroup, Tag, Workflow, WorkflowProcessPriority } from "@prisma/client";
import { stringifyTimeFrame } from "components/generic-building-blocks/ProcessTimeFrameSelector";
import { useUser } from "context/UserContext";
import { startOfYesterday, endOfToday } from "date-fns";
import * as JsSearch from "js-search";
import _ from "lodash";
import { emojify } from "node-emoji";
import React from "react";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import useAssigneeOptions from "utils/useAssigneeOptions";

interface Props {
  filter: ProcessFilterZod;
  setFilter: (_: ProcessFilterZod) => void;
}

interface PropsWithData<T> extends Props {
  isLoading: boolean;
  data?: T;
}

export function StatusFilterInput({ filter, setFilter }: Props) {
  if (filter.type !== "STATUS") {
    throw Error("Invalid filter supplied");
  }
  const { isLoading, data } = trpc2.v2.dynamicStatus.list.useQuery();
  const options = (data ?? []).map((status) => ({
    label: status.label,
    value: status.label,
  }));
  options.push({ label: "Notification Only", value: "NOTIFICATION_ONLY" });
  return isLoading ? (
    <Skeleton className="min-w-[300px] text-4xl" />
  ) : (
    <div className="min-w-[300px]">
      <DropdownMultiSelect
        name="statusFilter"
        overflow="wrap"
        size="MEDIUM"
        selectedValues={filter.value ?? []}
        options={options}
        placeholder="Search statuses"
        onChange={(statuses) => {
          setFilter({ type: "STATUS", value: statuses ?? [] });
        }}
      />
    </div>
  );
}

export function PriorityFilterInput({ filter, setFilter }: Props) {
  if (filter.type !== "PRIORITY") {
    throw Error("Invalid filter supplied");
  }
  const options: { label: string; value: WorkflowProcessPriority | "NO_PRIORITY" }[] = Object.values(WorkflowProcessPriority).map((priority) => ({
    label: _.capitalize(priority),
    value: priority,
  }));
  options.push({ label: "No Priority", value: "NO_PRIORITY" });
  return (
    <div className="min-w-[300px]">
      <DropdownMultiSelect
        name="priorityFilter"
        size="MEDIUM"
        overflow="wrap"
        selectedValues={filter.value ?? []}
        options={options}
        placeholder="Search priorities"
        onChange={(priorities) => {
          setFilter({ type: "PRIORITY", value: priorities ?? [] });
        }}
      />
    </div>
  );
}

export function AssigneeFilterInput({ filter, setFilter }: Props) {
  if (filter.type !== "ASSIGNEE") {
    throw Error("Invalid filter supplied");
  }
  const { userAssignees, teams, isLoading } = useAssigneeOptions();

  const userIds = new Set(userAssignees?.map((user) => user.id));
  const teamIds = new Set(teams?.map((team) => team.id));

  return isLoading ? (
    <Skeleton className="min-w-[200px] text-4xl" />
  ) : (
    <FilterAssigneeDropdown
      userAssignees={userAssignees}
      teams={teams}
      value={filter?.value?.map((assignee) => assignee.value.assigneeId ?? assignee.value.teamId ?? assignee.value.assigneeTemplate ?? "")}
      setValue={(selectedOptions: string[]) =>
        setFilter({
          type: "ASSIGNEE",
          value:
            selectedOptions?.map((option) => {
              if (userIds.has(option)) {
                return {
                  value: {
                    assigneeType: "USER",
                    assigneeId: option,
                  },
                  label: option,
                };
              }
              if (teamIds.has(option)) {
                return {
                  value: {
                    assigneeType: "TEAM",
                    teamId: option,
                  },
                  label: option,
                };
              } else {
                return {
                  value: {
                    assigneeType: "TEMPLATE",
                    assigneeTemplate: option,
                  },
                  label: option,
                };
              }
            }) ?? [],
        })
      }
    />
  );
}

export function EscalationGroupFilterInput({ filter, setFilter }: Props) {
  if (filter.type !== "ESCALATION_GROUP") {
    throw Error("Invalid filter supplied");
  }
  const { isLoading, data } = trpc2.v2.workflows.list.useQuery();

  const maxEscalationGroups = _.max(data?.map((workflow: ClientWorkflow) => workflow.notificationLevels?.length)) ?? 0;

  return isLoading ? (
    <Skeleton className="min-w-[200px] text-4xl" />
  ) : !data || data.length === 0 ? (
    <div className="text-xs flex flex-col gap-y-1 text-gray-500 text-center px-2">
      <div>We could not find any possible escalation groups to filter on.</div>
    </div>
  ) : (
    <div className="min-w-[300px]">
      <DropdownSelect
        name="escalationGroupFilter"
        size="MEDIUM"
        options={_.range(maxEscalationGroups).map((value) => {
          const groupLevel = value + 1;
          return { label: groupLevel.toString(), value: groupLevel.toString() };
        })}
        selectedValue={filter.value?.toString()}
        disableUnselect
        placeholder="Search escalation levels"
        onChange={(value) => {
          if (value) {
            setFilter({ type: "ESCALATION_GROUP", value: [value ? parseInt(value.replace(/[^(0-9|.)]/g, "")) : undefined] } as ProcessFilterZod);
          }
        }}
      />
    </div>
  );
}

export function WorkflowFilterInput({ filter, setFilter }: Props) {
  const { isLoading, data } = trpc2.v2.workflows.list.useQuery();
  if (filter.type !== "WORKFLOW") {
    throw Error("Invalid fiter supplied");
  }

  const options = data?.map((workflow: ClientWorkflow) => {
    return { label: emojify(`${workflow?.emoji ?? ""} ${workflow.title}`), value: workflow.id! };
  });

  return isLoading ? (
    <Skeleton className="min-w-[200px] text-4xl" />
  ) : !options || options.length === 0 ? (
    <div className="text-xs flex flex-col gap-y-1 text-gray-500 text-center px-2">
      <div>We could not find any workflows to filter on.</div>
    </div>
  ) : (
    <div className="min-w-[300px]">
      <DropdownMultiSelect
        name="workflowFilter"
        size="MEDIUM"
        overflow="wrap"
        selectedValues={filter.value ?? []}
        options={options ?? []}
        placeholder="Search monitors"
        onChange={(workflowIds) => {
          setFilter({ type: "WORKFLOW", value: workflowIds ?? [] });
        }}
      />
    </div>
  );
}

export function MonitorOwnerFilterInput({ filter, setFilter }: Props) {
  const { isLoading, data } = trpc.organizationMemberships.list.useQuery();
  if (filter.type !== "MONITOR_OWNER") {
    throw Error("Invalid fiter supplied");
  }

  const options = data
    ?.filter((member) => member.role === "ADMIN" || member.role === "CREATOR")
    .map((member) => {
      return {
        label: `${member.user.firstName} ${member.user.lastName}`,
        value: encodeURIComponent(
          JSON.stringify({
            label: `${member.user.firstName} ${member.user.lastName}`,
            value: member.user.id,
          }),
        ),
      };
    });

  const [values, setValues] = useState<string[]>(
    filter?.value?.map((option) => {
      return encodeURIComponent(JSON.stringify(option));
    }) ?? [],
  );

  return isLoading ? (
    <Skeleton className="min-w-[200px] text-4xl" />
  ) : !options || options.length === 0 ? (
    <div className="text-xs flex flex-col gap-y-1 text-gray-500 text-center px-2">
      <div>We could not find any owners to filter on.</div>
    </div>
  ) : (
    <div className="min-w-[300px]">
      <DropdownMultiSelect
        options={options}
        size="MEDIUM"
        selectedValues={values}
        placeholder="Search owners"
        onChange={(selectedUsers) => {
          setFilter({
            type: "MONITOR_OWNER",
            value:
              selectedUsers?.map((value: string) => {
                return JSON.parse(decodeURIComponent(value));
              }) ?? [],
          });
          setValues(selectedUsers ?? []);
        }}
        name="monitorOwnerFilter"
        overflow="wrap"
      />
    </div>
  );
}

export function TimeToResolutionFilterInput({ filter, setFilter }: Props) {
  if (filter.type !== "TIME_TO_RESOLUTION") {
    throw Error("Invalid filter supplied");
  }
  return (
    <>
      <div className="w-fit">
        <TextInput
          name="timeToResolutionNumberFilter"
          placeholder="24"
          size="MEDIUM"
          defaultValue={filter.value?.toString()}
          onChange={(e) => {
            if (e.target.value) {
              setFilter({ ...filter, value: e.target.value ? parseInt(e.target.value.replace(/[^(0-9|.)]/g, "")) : undefined } as ProcessFilterZod);
            }
          }}
        />
      </div>

      <div className="w-fit">
        <DropdownSelect
          name="timeToResolutionTimePeriodFilter"
          options={Object.keys(TimeToResolutionTimePeriods).map((timePeriod) => ({
            label: _.lowerCase(timePeriod),
            value: timePeriod,
          }))}
          size="MEDIUM"
          selectedValue={filter.secondValue}
          disableUnselect
          onChange={(secondValue) => {
            if (secondValue) {
              setFilter({ ...filter, secondValue: secondValue as TimeToResolutionTimePeriods });
            }
          }}
        />
      </div>
    </>
  );
}

export function CompletedAtFilterInput({ filter, setFilter }: Props) {
  if (filter.type !== "COMPLETED_AT") {
    throw Error("Invalid filter supplied");
  }

  const dateConvertedValue = filter.value ? new Date(filter.value) : undefined;
  const dateConvertedSecondValue = filter.secondValue ? new Date(filter.secondValue) : undefined;

  return (
    <>
      <div className="min-w-[120px]">
        <DropdownSelect
          name="completedAtFilterRelation"
          size="MEDIUM"
          selectedValue={filter.relation?.toString()}
          onChange={(value) => setFilter({ ...filter, relation: value } as ProcessFilterZod)}
          options={CompletedAtFilterRelations.map((relation) => {
            return {
              label: stringifyCompletedAtFilterRelation(relation),
              value: relation,
            };
          })}
        />
      </div>

      <div className="w-[200px]">
        <DateTimePicker
          name="firstValueCompletedAtFilter"
          selectedValue={dateConvertedValue}
          showTimeInput
          size="MEDIUM"
          onChange={(value) => {
            if (value) {
              setFilter({ ...filter, value: value.toISOString() });
            }
          }}
        />
      </div>

      {filter.relation === "BETWEEN" ? (
        <div className="inline-flex items-center z-10 gap-2">
          <div className="text-gray-500 font-semibold text-sm">and</div>
          <div className="w-[200px]">
            <DateTimePicker
              name="secondValueCompletedAtFilter"
              selectedValue={dateConvertedSecondValue}
              showTimeInput
              size="MEDIUM"
              onChange={(secondValue) => {
                if (secondValue) {
                  setFilter({ ...filter, secondValue: secondValue.toISOString() });
                }
              }}
            />
          </div>
        </div>
      ) : undefined}
    </>
  );
}

export function CreatedAtFilterInput({ filter, setFilter }: Props) {
  if (filter.type !== "CREATED_AT") {
    throw Error("Invalid filter supplied");
  }

  const user = useUser();

  const updateTimeFrame = (newTimeFrame: CreatedAtRelations) => {
    if (newTimeFrame === "CUSTOM") {
      setFilter({ type: "CREATED_AT", value: newTimeFrame, customStart: startOfYesterday().toISOString(), customEnd: endOfToday().toISOString() });
    } else {
      setFilter({ type: "CREATED_AT", value: newTimeFrame, customStart: undefined, customEnd: undefined });
    }
  };

  const updateTimeFrameStartIncl = (newTimeFrameStartIncl: Date | null) => {
    if (newTimeFrameStartIncl) {
      setFilter({ ...filter, customStart: newTimeFrameStartIncl.toISOString() });
    }
  };

  const updateTimeFrameEndExcl = (newTimeFrameEndExcl: Date | null) => {
    if (newTimeFrameEndExcl) {
      setFilter({ ...filter, customEnd: newTimeFrameEndExcl.toISOString() });
    }
  };

  return (
    <>
      <div className="min-w-[200px]">
        <DropdownSelect
          name="createdAtRelationSelect"
          options={Object.values(CreatedAtRelations).map((timeFrameOption: CreatedAtRelations) => ({
            label: stringifyTimeFrame(timeFrameOption),
            value: timeFrameOption,
          }))}
          selectedValue={filter.value?.toString()}
          disableUnselect
          size="MEDIUM"
          onChange={(value: string | undefined) => updateTimeFrame(value as CreatedAtRelations)}
        />
      </div>

      {filter.value?.toString() === "CUSTOM" ? (
        <div className="inline-flex items-center z-10 gap-2">
          <DateTimePicker
            name="createdAtDateFrom"
            selectedValue={filter.customStart ? new Date(filter.customStart) : undefined}
            onChange={updateTimeFrameStartIncl}
            timeZone={user.timeZone ?? undefined}
            size="MEDIUM"
            showTimeInput
          />
          <div className="text-gray-400 font-semibold text-sm">to</div>
          <DateTimePicker
            name="createdAtToDate"
            selectedValue={filter.customEnd ? new Date(filter.customEnd) : undefined}
            onChange={updateTimeFrameEndExcl}
            timeZone={user.timeZone ?? undefined}
            size="MEDIUM"
            showTimeInput
          />
        </div>
      ) : undefined}
    </>
  );
}

export function TagFilterInput({ filter, setFilter }: Props) {
  if (filter.type !== "TAG") {
    throw Error("Invalid filter supplied");
  }
  const { isLoading, data } = trpc.tags.getOrgTags.useQuery();

  const options = data?.map((tag: ClientTag) => {
    return { label: tag.name, value: tag.id };
  });

  return isLoading ? (
    <Skeleton className="min-w-[200px] text-4xl" />
  ) : !options || options.length === 0 ? (
    <div className="text-xs flex flex-col gap-y-1 text-gray-500 text-center px-2">
      <div>We could not find any tags in your organization.</div>
      <div>Add some to use this filter 🙂</div>
    </div>
  ) : (
    <div className="min-w-[300px]">
      <DropdownMultiSelect
        name="tagFilter"
        selectedValues={filter.value ?? []}
        options={options}
        size="MEDIUM"
        placeholder="Search tags"
        onChange={(tagIds) => {
          setFilter({ type: "TAG", value: tagIds ?? [] });
        }}
      />
    </div>
  );
}

export function MonitorGroupFilterInput({ filter, setFilter }: Props) {
  if (filter.type !== "MONITOR_GROUP") {
    throw Error("Invalid filter supplied");
  }
  const { isLoading, data } = trpc.monitorGroups.list.useQuery();

  const options = data?.map((group: MonitorGroup) => {
    return { label: group.title, value: group.id };
  });

  return isLoading ? (
    <Skeleton className="min-w-[200px] text-4xl" />
  ) : !options || options.length === 0 ? (
    <div className="text-xs flex flex-col gap-y-1 text-gray-500 text-center px-2">No Monitor Groups in your organization.</div>
  ) : (
    <div className="min-w-[300px]">
      <DropdownMultiSelect
        name="monitorGroupFilter"
        selectedValues={filter.value ?? []}
        options={options}
        size="MEDIUM"
        placeholder="Search Monitor Groups"
        onChange={(groupIds) => {
          setFilter({ type: "MONITOR_GROUP", value: groupIds ?? [] });
        }}
      />
    </div>
  );
}

/**
 * Filter all of this organizations tag options based on the search value and what the object already has to generate
 * a list of appropriate options
 */
export function searchTag(tagOptions: Tag[], searchValue?: string): Tag[] {
  if (!searchValue) {
    return tagOptions;
  }
  const search = new JsSearch.Search("name");
  search.addIndex("name");
  search.addDocuments(tagOptions);

  return search.search(searchValue) as unknown[] as Tag[];
}
